import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X as XIcon } from "lucide-react";
import { useAuth } from "../Context/AuthContext";

const API_BASE = "https://apisellhub.com/api";

const AVAILABLE_GATEWAYS = [
    "Stripe",
    "PayPal",
    "Sellhub",
    "Now payment",
    "PayPal F&F",
    "Direct Crypto",
    "Mollie",
];

const emptyForm = {
    name: "",
    description: "",
    price: "",
    plan_days: "",
    max_products: "",
    max_stores: "",
    max_team_members: "",
    payment_gateways: [],
    allow_custom_domain: false,
    status: true,
};

const PlanFormDialog = ({ open, plan, onClose, onSuccess }) => {
    const { getToken } = useAuth();
    const isEdit = !!plan;

    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;

        if (plan) {
            let gateways = plan.payment_gateways ?? [];
            if (typeof gateways === "string") {
                try {
                    gateways = JSON.parse(gateways);
                } catch {
                    gateways = [];
                }
            }

            setForm({
                name: plan.name ?? "",
                description: plan.description ?? "",
                price: plan.price ?? "",
                plan_days: plan.plan_days ?? "",
                max_products: plan.max_products ?? "",
                max_stores: plan.max_stores ?? "",
                max_team_members: plan.max_team_members ?? "",
                payment_gateways: Array.isArray(gateways) ? gateways : [],
                allow_custom_domain: !!plan.allow_custom_domain,
                status: plan.status === undefined ? true : !!plan.status,
            });
        } else {
            setForm(emptyForm);
        }
    }, [plan, open]);

    const updateField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const toggleGateway = (gateway) => {
        setForm((prev) => {
            const current = prev.payment_gateways ?? [];
            if (current.includes(gateway)) {
                return { ...prev, payment_gateways: current.filter((g) => g !== gateway) };
            }
            return { ...prev, payment_gateways: [...current, gateway] };
        });
    };

    const validate = () => {
        if (!form.name.trim()) return "Name is required";
        if (form.price === "" || isNaN(Number(form.price)) || Number(form.price) < 0)
            return "Valid price is required";
        if (form.plan_days === "" || !Number.isInteger(Number(form.plan_days)) || Number(form.plan_days) <= 0)
            return "Plan duration (days) must be a positive integer";
        if (form.max_products === "" || !Number.isInteger(Number(form.max_products)) || Number(form.max_products) < 0)
            return "Max products must be a non-negative integer";
        if (form.max_stores === "" || !Number.isInteger(Number(form.max_stores)) || Number(form.max_stores) < 0)
            return "Max stores must be a non-negative integer";
        if (form.max_team_members === "" || !Number.isInteger(Number(form.max_team_members)) || Number(form.max_team_members) < 0)
            return "Max team members must be a non-negative integer";
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const err = validate();
        if (err) {
            toast.error(err);
            return;
        }

        const payload = {
            name: form.name.trim(),
            description: form.description.trim(),
            price: Number(form.price),
            plan_days: Number(form.plan_days),
            max_products: Number(form.max_products),
            max_stores: Number(form.max_stores),
            max_team_members: Number(form.max_team_members),
            payment_gateways: form.payment_gateways,
            allow_custom_domain: !!form.allow_custom_domain,
            status: !!form.status,
        };

        setSubmitting(true);
        try {
            const token = getToken();
            const url = isEdit
                ? `${API_BASE}/super-admin/pricing-plans/${plan.id}`
                : `${API_BASE}/super-admin/pricing-plans`;

            const response = await axios.put(url, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (response.data?.status !== false) {
                toast.success(isEdit ? "Plan updated" : "Plan created");
                onSuccess?.();
            } else {
                toast.error(response.data?.message ?? "Failed to save plan");
            }
        } catch (err) {
            console.error("Save plan error:", err);
            toast.error(err.response?.data?.message ?? "Failed to save plan");
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60"
                onClick={() => !submitting && onClose?.()}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-bold text-gray-900">
                        {isEdit ? "Edit Pricing Plan" : "New Pricing Plan"}
                    </h2>
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"
                    >
                        <XIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => updateField("name", e.target.value)}
                            placeholder="e.g. Starter"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                            value={form.description}
                            onChange={(e) => updateField("description", e.target.value)}
                            placeholder="Short summary of what this plan includes"
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Price + Duration */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Price (USD) *</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.price}
                                onChange={(e) => updateField("price", e.target.value)}
                                placeholder="29.00"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Duration (days) *</label>
                            <input
                                type="number"
                                min="1"
                                value={form.plan_days}
                                onChange={(e) => updateField("plan_days", e.target.value)}
                                placeholder="30"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    {/* Limits */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Max Products *</label>
                            <input
                                type="number"
                                min="0"
                                value={form.max_products}
                                onChange={(e) => updateField("max_products", e.target.value)}
                                placeholder="10"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Max Stores *</label>
                            <input
                                type="number"
                                min="0"
                                value={form.max_stores}
                                onChange={(e) => updateField("max_stores", e.target.value)}
                                placeholder="1"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Max Team Members *</label>
                            <input
                                type="number"
                                min="0"
                                value={form.max_team_members}
                                onChange={(e) => updateField("max_team_members", e.target.value)}
                                placeholder="2"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    {/* Payment Gateways */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Allowed Payment Gateways
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {AVAILABLE_GATEWAYS.map((gateway) => {
                                const selected = form.payment_gateways.includes(gateway);
                                return (
                                    <button
                                        type="button"
                                        key={gateway}
                                        onClick={() => toggleGateway(gateway)}
                                        className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                                            selected
                                                ? "bg-blue-600 text-white border-blue-600"
                                                : "bg-white text-gray-700 border-gray-300 hover:border-blue-400"
                                        }`}
                                    >
                                        {selected && <span className="mr-1">✓</span>}
                                        {gateway}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Toggles */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                        <label className="flex items-center justify-between p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300">
                            <div>
                                <div className="text-sm font-medium text-gray-900">Allow Custom Domain</div>
                                <div className="text-xs text-gray-500 mt-0.5">Sellers can use their own domain</div>
                            </div>
                            <input
                                type="checkbox"
                                checked={form.allow_custom_domain}
                                onChange={(e) => updateField("allow_custom_domain", e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                        </label>
                        <label className="flex items-center justify-between p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300">
                            <div>
                                <div className="text-sm font-medium text-gray-900">Active</div>
                                <div className="text-xs text-gray-500 mt-0.5">Available for sellers to purchase</div>
                            </div>
                            <input
                                type="checkbox"
                                checked={form.status}
                                onChange={(e) => updateField("status", e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                        </label>
                    </div>
                </form>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                        {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        {isEdit ? "Save Changes" : "Create Plan"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PlanFormDialog;
